import React, { useId } from 'react';

/* ─── Primitive field components ──────────────────────────────── */

const TextField = ({ id, type = 'text', value, onChange, disabled = false }) => (
  <input
    id={id}
    type={type}
    className="dotfw-field dotfw-field--text"
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    spellCheck={false}
    disabled={disabled}
  />
);

const TextAreaField = ({ id, value, onChange, disabled = false }) => (
  <textarea
    id={id}
    className="dotfw-field dotfw-field--textarea"
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    rows={4}
    spellCheck={false}
    disabled={disabled}
  />
);

const SelectField = ({ id, value, options, onChange, disabled = false }) => (
  <div className="dotfw-select-wrap">
    <select
      id={id}
      className="dotfw-field dotfw-field--select"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

const ToggleField = ({ id, checked, label, onChange, disabled = false }) => (
  <label className="dotfw-toggle" htmlFor={id}>
    <input
      id={id}
      type="checkbox"
      className="dotfw-toggle-input"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
    />
    <span className="dotfw-toggle-track" aria-hidden="true">
      <span className="dotfw-toggle-thumb" />
    </span>
    {label && <span className="dotfw-toggle-label">{label}</span>}
  </label>
);

const RadioGroupField = ({ name, value, options, onChange, disabled = false }) => (
  <div className="dotfw-radio-group" role="radiogroup">
    {options.map((opt) => {
      const isChecked = value === opt.value;
      return (
        <label
          key={opt.value}
          className={`dotfw-radio-option${isChecked ? ' is-checked' : ''}`}
        >
          <input
            type="radio"
            className="dotfw-radio-input"
            name={name}
            value={opt.value}
            checked={isChecked}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
          <span className="dotfw-radio-dot" aria-hidden="true" />
          <span className="dotfw-radio-text">{opt.label}</span>
        </label>
      );
    })}
  </div>
);

const ColorPickerField = ({ id, value, onChange, disabled = false }) => (
  <div className="dotfw-color-wrap">
    <label htmlFor={id} className="dotfw-color-swatch-btn">
      <span
        className="dotfw-color-swatch"
        style={{ background: value || '#a16207' }}
      />
      <span className="dotfw-color-hex">{value || '—'}</span>
      <span className="dotfw-color-hint">Click to change</span>
    </label>
    <input
      id={id}
      type="color"
      className="dotfw-color-native"
      value={value || '#a16207'}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    />
  </div>
);

/* ─── Main export ─────────────────────────────────────────────── */

/**
 * Renders a labelled input row inside a section card body.
 *
 * @param {string}   label         - Row label shown on the left.
 * @param {string}   key           - Settings key to read/write.
 * @param {string}   type          - text | textArea | checkBox | select | radioGroup | colorPicker
 * @param {Array}    options       - Options for select / radioGroup.
 * @param {string}   checkboxLabel - Label next to the toggle.
 * @param {object}   Settings      - Current settings state object.
 * @param {Function} updateSetting - Callback (key, value) => void.
 * @param {string}   description   - Optional helper text below the field.
 */
export const renderInputRow = (
  label,
  key,
  type        = 'text',
  options     = [],
  checkboxLabel = '',
  Settings    = {},
  updateSetting = () => {},
  description = '',
  disabled    = false,
) => {
  /* eslint-disable react-hooks/rules-of-hooks */
  // useId is stable and called at module level here — fine for a render helper
  // If lint complains, convert renderInputRow into a proper React component.
  const uid = `dotfw-${key}-${type}`;

  const renderField = () => {
    switch (type) {
      case 'select':
        return (
          <SelectField
            id={uid}
            value={Settings[key]}
            options={options}
            onChange={(val) => updateSetting(key, val)}
            disabled={disabled}
          />
        );

      case 'checkBox':
        return (
          <ToggleField
            id={uid}
            checked={Settings[key] === 'yes'}
            label={checkboxLabel || 'Enable'}
            onChange={(val) => updateSetting(key, val ? 'yes' : 'no')}
            disabled={disabled}
          />
        );

      case 'radioGroup':
        return (
          <RadioGroupField
            name={key}
            value={Settings[key]}
            options={options}
            onChange={(val) => updateSetting(key, val)}
            disabled={disabled}
          />
        );

      case 'textArea':
        return (
          <TextAreaField
            id={uid}
            value={Settings[key]}
            onChange={(val) => updateSetting(key, val)}
            disabled={disabled}
          />
        );

      case 'colorPicker':
        return (
          <ColorPickerField
            id={uid}
            value={Settings[key]}
            onChange={(val) => updateSetting(key, val)}
            disabled={disabled}
          />
        );

      default:
        return (
          <TextField
            id={uid}
            type={type}
            value={Settings[key]}
            onChange={(val) => updateSetting(key, val)}
            disabled={disabled}
          />
        );
    }
  };

  const isRadio = type === 'radioGroup';

  return (
    <div
  className={`dotfw-input-row${isRadio ? ' dotfw-input-row--radio' : ''}`}
  key={key}
>
  {/* Label column */}
  <label
    className="dotfw-input-row-label"
    htmlFor={isRadio ? undefined : uid}
  >
    {label}
  </label>

  {/* Field column */}
  <div
    className={`dotfw-input-row-field${
      isRadio ? ' dotfw-input-row-field--radio' : ''
    }`}
    style={{ maxWidth: `${disabled ? '587px' : '360px'}`}}
  >
    <div className="dotfw-field-inline-wrap">
      {renderField()}

      {disabled && (
        <p className="dotfw-pro-notice">
          This option is available in{' '}
          <a
            href="https://www.topsmodule.com/product/donation-or-tip-for-woocommerce/"
            target="_blank"
            rel="noreferrer"
          >
            Pro Version
          </a>
        </p>
      )}
    </div>

    {description && (
      <p className="dotfw-input-description">{description}</p>
    )}
  </div>
</div>
  );
};